import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import {
  createResearchQueryRunScoringConfig,
  getResearchQueryRunScoringConfigById,
  updateResearchQueryRunScoringConfig,
} from "../../services/scoringService";
import useFetchResearchQueryRunScoringConfigById from "../../hooks/useFetchResearchQueryRunScoringConfigById";
import {
  researchQueryRunScoringConfigDefaults,
  scoringSeverityOptions,
} from "../../services/constants";
import { runScoringConfigFormSchema } from "../../schemas";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import AutoCompleteRunList from "../../components/Molecules/Run/AutoCompleteRunList";
import AutoCompleteGenericResearchQueryId from "../../components/Molecules/GenericResearchQuery/AutoCompleteGenericResearchQueryId";
import { getRunInstanceDetail } from "../../services/runInstance";
import { getGenericResearchQueryDetail } from "../../services/ResearchQueriesService";

const AddEditResearchQueryRunScoring: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { id: configId } = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const isEditMode = Boolean(configId);

  const [isLoading, setIsLoading] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [runInstanceId, setRunInstanceId] = useState<number | "">("");
  const [runInstanceError, setRunInstanceError] = useState<string>("");
  const [genericResearchQueryId, setGenericResearchQueryId] = useState<
    number | ""
  >("");
  const [genericResearchQueryError, setGenericResearchQueryError] =
    useState<string>("");
  const [runInstanceName, setRunInstanceName] = useState<string>("");
  const [genericResearchQueryName, setGenericResearchQueryName] =
    useState<string>("");

  const [configData] = useFetchResearchQueryRunScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });
  const [localConfigOverride, setLocalConfigOverride] = useState<any>(null);
  const effectiveConfig =
    localConfigOverride ||
    (configData?.id ? configData : null) ||
    (isEditMode && location.state?.rowData?.id ? location.state.rowData : null);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  const formik = useFormik({
    initialValues: {
      severity:
        effectiveConfig?.severity ??
        researchQueryRunScoringConfigDefaults.severity,
      factor:
        effectiveConfig?.factor ?? researchQueryRunScoringConfigDefaults.factor,
    },
    validationSchema: runScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!isEditMode) {
        let hasEntityError = false;
        if (!runInstanceId) {
          setRunInstanceError("Execution Group is required");
          hasEntityError = true;
        }
        if (!genericResearchQueryId) {
          setGenericResearchQueryError("Research Query is required");
          hasEntityError = true;
        }
        if (hasEntityError) return;
        setIsLoading(true);
        try {
          await createResearchQueryRunScoringConfig({
            run_instance_id: Number(runInstanceId),
            generic_research_query_id: Number(genericResearchQueryId),
            severity: values.severity,
            factor: Number(values.factor),
          });
          showToast("Research Query Run Scoring Config created!", "success");
          navigate("/scoring-config?tab=1&aggSub=2");
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, "Failed to create config"),
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        const diff = getDifferentValues(values, formik.initialValues);
        if (!diff || Object.keys(diff).length === 0) {
          showToast("No changes detected", "warning");
          return;
        }
        const coerced: any = {};
        Object.keys(values).forEach((k) => {
          const v = (values as any)[k];
          coerced[k] = k === "factor" ? Number(v) : v;
        });
        setPendingPayload(coerced);
        setOpenComment(true);
      }
    },
  });

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: isEditMode
        ? "Edit Research Query Run Scoring"
        : "Add Research Query Run Scoring",
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (effectiveConfig?.run_instance_id) {
      setRunInstanceId(effectiveConfig.run_instance_id);
    }
    if (effectiveConfig?.generic_research_query_id) {
      setGenericResearchQueryId(effectiveConfig.generic_research_query_id);
    }
  }, [effectiveConfig]);

  // Fetch entity display names for read-only edit-mode fields
  useEffect(() => {
    if (!isEditMode || !runInstanceId) return;
    (async () => {
      try {
        const data = await getRunInstanceDetail({
          currentRunInstanceId: runInstanceId,
        });
        if (data?.run_name) setRunInstanceName(data.run_name);
      } catch (err) {
        console.error("Failed to load execution group name:", err);
      }
    })();
  }, [isEditMode, runInstanceId]);

  useEffect(() => {
    if (!isEditMode || !genericResearchQueryId) return;
    (async () => {
      try {
        const data = await getGenericResearchQueryDetail(
          genericResearchQueryId as number
        );
        const name = data?.name || data?.generic_research_query?.name;
        if (name) setGenericResearchQueryName(name);
      } catch (err) {
        console.error("Failed to load generic research query name:", err);
      }
    })();
  }, [isEditMode, genericResearchQueryId]);

  const buildPayload = () => {
    if (isEditMode) {
      // Mirrors the body sent by handleSaveComment to updateResearchQueryRunScoringConfig.
      // Uses the live `comment` state so the View JSON always reflects the
      // exact payload that would be sent if Save were clicked right now.
      return {
        severity: formik.values.severity,
        factor: Number(formik.values.factor),
        comment,
      };
    }
    return {
      run_instance_id: Number(runInstanceId),
      generic_research_query_id: Number(genericResearchQueryId),
      severity: formik.values.severity,
      factor: Number(formik.values.factor),
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/research-query-run-scoring-config/${configId}`
          : "/research-query-run-scoring-config",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: configId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) showToast("Payload is valid!", "success");
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(buildPayload(), null, 2)
    );
    showToast("Payload copied to clipboard!", "success");
  };

  const handleChangeRunInstance = (_e: any, v: any) => {
    setRunInstanceId(v?.id ?? "");
    if (runInstanceError) setRunInstanceError("");
  };

  const handleChangeGenericResearchQuery = (_e: any, v: any) => {
    setGenericResearchQueryId(v?.id ?? "");
    if (genericResearchQueryError) setGenericResearchQueryError("");
  };

  const handleSaveComment = async () => {
    setOpenComment(false);
    setIsLoading(true);
    try {
      await updateResearchQueryRunScoringConfig(parseInt(configId!), {
        ...pendingPayload,
        comment,
      });
      showToast("Config updated!", "success");
      const refreshed = await getResearchQueryRunScoringConfigById(
        parseInt(configId!)
      );
      if (refreshed) {
        setLocalConfigOverride(refreshed);
      }
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update config"),
        "error"
      );
    } finally {
      setIsLoading(false);
      setComment("");
      setPendingPayload(null);
    }
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      severity: researchQueryRunScoringConfigDefaults.severity,
      factor: researchQueryRunScoringConfigDefaults.factor,
    });
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>
                {isEditMode ? "Edit" : "Add"} Research Query Run Scoring Config
              </h3>
            </Box>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Execution Group</label>
                    <TextField
                      fullWidth
                      value={
                        runInstanceName
                          ? `(${runInstanceId}) ${runInstanceName}`
                          : runInstanceId
                          ? `(${runInstanceId})`
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteRunList
                    setIsLoading={setIsLoading}
                    handelOnChangeRun={handleChangeRunInstance}
                    currentRunId={runInstanceId || null}
                    className={`form-control-autocomplete${
                      runInstanceError ? " has-error" : ""
                    }`}
                    required
                    error={Boolean(runInstanceError)}
                    helperText={runInstanceError}
                    listLabel="Execution Group"
                    includeRunId
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Generic Research Query</label>
                    <TextField
                      fullWidth
                      value={
                        genericResearchQueryName
                          ? `(${genericResearchQueryId}) ${genericResearchQueryName}`
                          : genericResearchQueryId
                          ? `(${genericResearchQueryId})`
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteGenericResearchQueryId
                    setIsLoading={setIsLoading}
                    handelOnChangeGenericResearchQuery={
                      handleChangeGenericResearchQuery
                    }
                    currentGenericResearchQueryId={
                      genericResearchQueryId || null
                    }
                    filterByScoreConfigAssociated={false}
                    className={`form-control-autocomplete${
                      genericResearchQueryError ? " has-error" : ""
                    }`}
                    required
                    error={Boolean(genericResearchQueryError)}
                    helperText={genericResearchQueryError}
                    listLabel="Generic Research Query"
                    includeGenericResearchQueryId
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <label className="label-text">
                  Severity<span className="required-asterisk">*</span>
                </label>
                <TextField
                  select
                  name="severity"
                  value={formik.values.severity || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-control dropdown-padding-1${
                    formik.touched.severity && formik.errors.severity
                      ? " has-error"
                      : ""
                  }`}
                  error={
                    formik.touched.severity && Boolean(formik.errors.severity)
                  }
                  helperText={
                    (formik.touched.severity &&
                      (formik.errors.severity as string)) ||
                    " "
                  }
                  fullWidth
                >
                  {scoringSeverityOptions.map((opt: string) => (
                    <MenuItem key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <label className="label-text">
                  Factor<span className="required-asterisk">*</span>
                </label>
                <TextField
                  type="number"
                  name="factor"
                  value={formik.values.factor ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-control${
                    formik.errors.factor ? " has-error" : ""
                  }`}
                  error={Boolean(formik.errors.factor)}
                  helperText={(formik.errors.factor as string) || " "}
                  inputProps={{ step: "0.01", min: "0", max: "1" }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <FlexBetween gap={2.5} sx={{ mt: 1, justifyContent: "flex-end" }}>
            <Button
              type="button"
              className="btn-orange btn-dark"
              variant="contained"
              disabled={formik.isSubmitting}
              onClick={handleResetToDefaults}
              startIcon={<RestoreIcon />}
            >
              Reset to Defaults
            </Button>
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={
                formik.isSubmitting ||
                !formik.isValid ||
                (isEditMode && !formik.dirty)
              }
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              Save
            </Button>
          </FlexBetween>
        </form>
      </Box>
      <CommentBeforeUpdateDialog
        title="Confirm updating Research Query Run Scoring Config"
        dialogContent={
          <div>
            <p className="m-0 mb-2">Add a note before updating</p>
            <textarea
              value={comment}
              className="form-control-1 max-60"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        }
        openConfirmation={openComment}
        handleSaveComment={handleSaveComment}
        handleCancel={() => {
          setOpenComment(false);
          setComment("");
          setPendingPayload(null);
        }}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Research Query Run Scoring Config Payload"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default AddEditResearchQueryRunScoring;
